
import {
  computed, defineComponent, ref,
} from 'vue';
import { useSelecionarBase } from '@/core/composables/SelecionarBase';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import ServicoCnae from '@/servicos/Cadastros/PrestacaoServicos/ServicoCnae';

export default defineComponent({
  name: 'SelecionarCnae',
  props: {
    codigoSelecionado: {
      type: Number || undefined,
    },
    codigoCnaeSelecionado: {
      type: String,
      default: '',
    },
    codigoItemListaSelecionado: {
      type: String,
      default: '',
    },
    codigoTribMunicipioSelecionado: {
      type: String,
      default: '',
    },
    descricaoSelecionado: {
      type: String,
      default: '',
    },
    empresas: {
      type: Array as () => number[],
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    dataAttributeColuna: {
      type: String,
      default: '',
    },
    classCss: {
      type: String,
      default: '',
    },
    limparSelecao: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['update:codigoSelecionado', 'update:codigosSelecionados', 'update:descricaoSelecionado', 'update:codigoItemListaSelecionado', 'update:codigoTribMunicipioSelecionado', 'update:codigoCnaeSelecionado', 'change'],
  setup(props, { emit }) {
    const {
      selecionarBase, montaOpcoesComListaConsultaRapida, verificacaoPreBusca, verificacaoUltimaPesquisa, aguardarConclusaoCarregamento,
      comportamentoPadraoSemResultado, instanciaParametrosConsultaRapidaPorCodigo, instanciaParametrosConsultaRapidaPesquisa,
    } = useSelecionarBase(props, emit);

    const refSelecionar = ref<HTMLElement | null>(null);

    let debounce = 0;
    const servico = new ServicoCnae();

    function preencheListaOpcoes(): void {
      if (UtilitarioGeral.validaLista(selecionarBase.listaItensConsultaRapida)) {
        selecionarBase.listaOpcoes = montaOpcoesComListaConsultaRapida(selecionarBase.listaItensConsultaRapida);
      }
    }

    async function obterRegistroPorCodigo(valor: any) {
      if (!verificacaoPreBusca(valor)) return;

      selecionarBase.listaOpcoes = [];

      const parametrosConsultaRapida = instanciaParametrosConsultaRapidaPorCodigo(valor, true);
      selecionarBase.listaItensConsultaRapida = await servico.consultaRapida(parametrosConsultaRapida);
      preencheListaOpcoes();
      comportamentoPadraoSemResultado();
    }

    async function pesquisarRegistro(valorPesquisa: any) {
      selecionarBase.emDigitacao = true;

      if (!verificacaoUltimaPesquisa(valorPesquisa)) return;
      selecionarBase.buscandoDados = true;
      selecionarBase.listaOpcoes = [];
      clearTimeout(debounce);
      debounce = setTimeout(async () => {
        const parametrosConsultaRapida = instanciaParametrosConsultaRapidaPesquisa(valorPesquisa, true);
        selecionarBase.listaItensConsultaRapida = await servico.consultaRapida(parametrosConsultaRapida);
        preencheListaOpcoes();
        selecionarBase.valorUltimaPesquisa = valorPesquisa;
        selecionarBase.buscandoDados = false;
      }, 600);
    }

    function obterRegistro(): any {
      obterRegistroPorCodigo(props.codigoSelecionado);
      return (props.codigoSelecionado === 0 ? undefined : props.codigoSelecionado);
    }

    const computedDescricaoSelecionado = computed({
      get: () => props.descricaoSelecionado,
      set: (val: string) => {
        emit('update:descricaoSelecionado', val);
      },
    });

    const computedCodigoCnaeSelecionado = computed({
      get: () => props.codigoCnaeSelecionado,
      set: (val: string) => {
        emit('update:codigoCnaeSelecionado', val);
      },
    });

    const computedCodigoItemListaSelecionado = computed({
      get: () => props.codigoItemListaSelecionado,
      set: (val: string) => {
        emit('update:codigoItemListaSelecionado', val);
      },
    });

    const computedCodigoTribMunicipioSelecionado = computed({
      get: () => props.codigoItemListaSelecionado,
      set: (val: string) => {
        emit('update:codigoTribMunicipioSelecionado', val);
      },
    });

    const computedCodigoSelecionado = computed({
      get: () => obterRegistro(),
      set: async (val: number) => {
        emit('update:codigoSelecionado', val);
        const itemConsulta = selecionarBase.listaItensConsultaRapida.find((c) => c.codigo === val);
        if (itemConsulta?.textoIdentificacao !== undefined && itemConsulta?.textoIdentificacao !== '') {
          const cnae = await servico.obter(val, props.empresas[0]);
          computedCodigoCnaeSelecionado.value = cnae.codigoCnae;
          computedCodigoItemListaSelecionado.value = cnae.codigoServico;
          computedCodigoTribMunicipioSelecionado.value = cnae.codigoTributacaoMunicipio;
          computedDescricaoSelecionado.value = cnae.descricao;
        }
      },
    });

    function change() {
      emit('change', props.codigoSelecionado);
    }

    function focus() {
      if (refSelecionar.value) {
        refSelecionar.value.focus();
      }
    }

    return {
      props,
      selecionarBase,
      refSelecionar,
      pesquisarRegistro,
      aguardarConclusaoCarregamento,
      computedCodigoSelecionado,
      computedDescricaoSelecionado,
      computedCodigoCnaeSelecionado,
      computedCodigoItemListaSelecionado,
      computedCodigoTribMunicipioSelecionado,
      change,
      focus,
      UtilitarioGeral,
    };
  },
});
